/**
 * Browser → Mux direct upload via UpChunk (Mux's official uploader:
 * chunked, resumable within the page's lifetime, retrying — a plain
 * PUT of multi-GB footage drops partway with a bare network error).
 *
 * Extracted from FolioPage's putToMuxWithProgress so the A-Roll SaaS
 * uploader can drive pause/resume; FolioPage keeps its local copy
 * untouched (refactor-later, not in this stream of work).
 *
 * PROGRESS HONESTY (Nick, 2026-07-30: "the progress bar wasn't
 * accurate, went from 3% to 100"). UpChunk only reports progress when a
 * chunk finishes, and `dynamicChunkSize` grows chunks as the connection
 * allows — so a multi-GB file uploaded in a handful of very large
 * chunks sat at 3% and then jumped to done. A fixed, moderate chunk
 * size trades a little adaptive cleverness for a bar that actually
 * moves: 8 MB means ~300 ticks on a 2.4 GB render instead of ~5, and a
 * failed chunk costs 8 MB to retry rather than hundreds.
 */
import { createUpload } from '@mux/upchunk';

/** UpChunk takes chunkSize in KB and requires a multiple of 256. */
const CHUNK_KB = 8192;

export type MuxUploadProgress = {
  /** 0-100, rounded. */
  pct: number;
  uploadedBytes: number;
  totalBytes: number;
  /** Rolling average since the upload started; null until measurable. */
  bytesPerSec: number | null;
  /** Seconds remaining at the current average; null until measurable. */
  etaSec: number | null;
};

export type MuxUploadHandle = {
  /** Resolves on success, rejects on a fatal upload error or abort. */
  done: Promise<void>;
  pause: () => void;
  resume: () => void;
  abort: () => void;
};

export function uploadToMux(
  url: string,
  file: File,
  onProgress: (pct: number, detail?: MuxUploadProgress) => void,
): MuxUploadHandle {
  const upload = createUpload({
    endpoint: url,
    file,
    chunkSize: CHUNK_KB,
  });
  const startedAt = Date.now();
  const done = new Promise<void>((resolve, reject) => {
    upload.on('progress', (e) => {
      const pct = Math.max(0, Math.min(100, Math.round((e.detail as number) ?? 0)));
      const uploadedBytes = Math.round((pct / 100) * file.size);
      const elapsed = (Date.now() - startedAt) / 1000;
      // Only quote a rate once there's enough signal for it to mean
      // anything; a first-chunk figure is mostly connection warm-up.
      const bytesPerSec = elapsed > 2 && uploadedBytes > 0 ? uploadedBytes / elapsed : null;
      const etaSec =
        bytesPerSec && bytesPerSec > 0
          ? Math.round((file.size - uploadedBytes) / bytesPerSec)
          : null;
      onProgress(pct, {
        pct,
        uploadedBytes,
        totalBytes: file.size,
        bytesPerSec,
        etaSec,
      });
    });
    upload.on('success', () => resolve());
    upload.on('error', (e) => {
      const detail = e.detail as { message?: string } | undefined;
      reject(new Error(detail?.message ? `Upload failed: ${detail.message}` : 'Upload failed'));
    });
  });
  return {
    done,
    pause: () => upload.pause(),
    resume: () => upload.resume(),
    abort: () => upload.abort(),
  };
}

/** "1.4 GB" / "820 MB" — upload copy, not file-manager precision. */
export function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

/** "4 min left" / "40 sec left" — coarse on purpose; a bouncing
 *  second-by-second estimate reads as broken. */
export function formatEta(sec: number): string {
  if (sec >= 5400) return `${Math.round(sec / 3600)} hr left`;
  if (sec >= 90) return `${Math.round(sec / 60)} min left`;
  return `${Math.max(5, Math.round(sec / 5) * 5)} sec left`;
}
